import { useState } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Camera, Sun, ImageSquare, SlidersHorizontal, Info, CalendarCheck, Backpack } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { FILM_CAMERAS, isFilmCameraId, getFilmCameraLabel } from "@/data/filmCameras";
import { cameraType } from "@/components/CameraTypeTag";

const CAMERA_GROUPS = [
  {
    brand: "Canon",
    cameras: [
      { value: "canon_eos_rebel_t3i", labelKey: "cam_canon_eos_rebel_t3i" },
      { value: "canon_eos_rebel_t7", labelKey: "cam_canon_eos_rebel_t7" },
      { value: "canon_eos_rebel_t8i", labelKey: "cam_canon_eos_rebel_t8i" },
      { value: "canon_eos_20d", labelKey: "cam_canon_eos_20d" },
      { value: "canon_eos_80d", labelKey: "cam_canon_eos_80d" },
      { value: "canon_eos_90d", labelKey: "cam_canon_eos_90d" },
      { value: "canon_eos_250d", labelKey: "cam_canon_eos_250d" },
      { value: "canon_eos_4000d", labelKey: "cam_canon_eos_4000d" },
      { value: "canon_eos_5d_mark_iii", labelKey: "cam_canon_eos_5d_mark_iii" },
      { value: "canon_eos_5d_mark_iv", labelKey: "cam_canon_eos_5d_mark_iv" },
      { value: "canon_eos_6d_mark_ii", labelKey: "cam_canon_eos_6d_mark_ii" },
      { value: "canon_eos_1dx_mark_iii", labelKey: "cam_canon_eos_1dx_mark_iii" },
      { value: "canon_eos_r", labelKey: "cam_canon_eos_r" },
      { value: "canon_eos_rp", labelKey: "cam_canon_eos_rp" },
      { value: "canon_eos_r3", labelKey: "cam_canon_eos_r3" },
      { value: "canon_eos_r5", labelKey: "cam_canon_eos_r5" },
      { value: "canon_eos_r6", labelKey: "cam_canon_eos_r6" },
      { value: "canon_eos_r7", labelKey: "cam_canon_eos_r7" },
      { value: "canon_eos_r8", labelKey: "cam_canon_eos_r8" },
      { value: "canon_eos_r10", labelKey: "cam_canon_eos_r10" },
      { value: "canon_eos_r50", labelKey: "cam_canon_eos_r50" },
      { value: "canon_eos_r100", labelKey: "cam_canon_eos_r100" },
    ],
  },
  {
    brand: "Nikon",
    cameras: [
      { value: "nikon_d3200", labelKey: "cam_nikon_d3200" },
      { value: "nikon_d3500", labelKey: "cam_nikon_d3500" },
      { value: "nikon_d5600", labelKey: "cam_nikon_d5600" },
      { value: "nikon_d7500", labelKey: "cam_nikon_d7500" },
      { value: "nikon_d750", labelKey: "cam_nikon_d750" },
      { value: "nikon_d780", labelKey: "cam_nikon_d780" },
      { value: "nikon_d850", labelKey: "cam_nikon_d850" },
      { value: "nikon_d6", labelKey: "cam_nikon_d6" },
      { value: "nikon_coolpix_p100", labelKey: "cam_nikon_coolpix_p100" },
      { value: "nikon_z9", labelKey: "cam_nikon_z9" },
      { value: "nikon_z8", labelKey: "cam_nikon_z8" },
      { value: "nikon_z7ii", labelKey: "cam_nikon_z7ii" },
      { value: "nikon_z6iii", labelKey: "cam_nikon_z6iii" },
      { value: "nikon_z5", labelKey: "cam_nikon_z5" },
      { value: "nikon_zf", labelKey: "cam_nikon_zf" },
      { value: "nikon_z50", labelKey: "cam_nikon_z50" },
      { value: "nikon_zfc", labelKey: "cam_nikon_zfc" },
      { value: "nikon_z30", labelKey: "cam_nikon_z30" },
    ],
  },
  {
    brand: "Pentax",
    cameras: [
      { value: "pentax_k70", labelKey: "cam_pentax_k70" },
      { value: "pentax_kf", labelKey: "cam_pentax_kf" },
    ],
  },
  {
    brand: "Sony",
    cameras: [
      { value: "sony_a58", labelKey: "cam_sony_a58" },
      { value: "sony_a68", labelKey: "cam_sony_a68" },
      { value: "sony_a77_ii", labelKey: "cam_sony_a77_ii" },
      { value: "sony_a99ii", labelKey: "cam_sony_a99ii" },
      { value: "sony_a1", labelKey: "cam_sony_a1" },
      { value: "sony_a9iii", labelKey: "cam_sony_a9iii" },
      { value: "sony_a7rv", labelKey: "cam_sony_a7rv" },
      { value: "sony_a7iv", labelKey: "cam_sony_a7iv" },
      { value: "sony_a7siii", labelKey: "cam_sony_a7siii" },
      { value: "sony_a7cii", labelKey: "cam_sony_a7cii" },
      { value: "sony_zve1", labelKey: "cam_sony_zve1" },
      { value: "sony_a6700", labelKey: "cam_sony_a6700" },
      { value: "sony_a6400", labelKey: "cam_sony_a6400" },
      { value: "sony_a6100", labelKey: "cam_sony_a6100" },
      { value: "sony_a6000", labelKey: "cam_sony_a6000" },
      { value: "sony_zve10", labelKey: "cam_sony_zve10" },
      { value: "sony_fx3", labelKey: "cam_sony_fx3" },
      { value: "sony_fx30", labelKey: "cam_sony_fx30" },
    ],
  },
  {
    brand: "Fujifilm",
    cameras: [
      { value: "fujifilm_x100vi", labelKey: "cam_fujifilm_x100vi" },
      { value: "fujifilm_xh2s", labelKey: "cam_fujifilm_xh2s" },
      { value: "fujifilm_xh2", labelKey: "cam_fujifilm_xh2" },
      { value: "fujifilm_xt5", labelKey: "cam_fujifilm_xt5" },
      { value: "fujifilm_xt50", labelKey: "cam_fujifilm_xt50" },
      { value: "fujifilm_xt4", labelKey: "cam_fujifilm_xt4" },
      { value: "fujifilm_xt3", labelKey: "cam_fujifilm_xt3" },
      { value: "fujifilm_xt2", labelKey: "cam_fujifilm_xt2" },
      { value: "fujifilm_xh1", labelKey: "cam_fujifilm_xh1" },
      { value: "fujifilm_xs20", labelKey: "cam_fujifilm_xs20" },
      { value: "fujifilm_xs10", labelKey: "cam_fujifilm_xs10" },
      { value: "fujifilm_xt30ii", labelKey: "cam_fujifilm_xt30ii" },
      { value: "fujifilm_xt30", labelKey: "cam_fujifilm_xt30" },
      { value: "fujifilm_xe4", labelKey: "cam_fujifilm_xe4" },
      { value: "fujifilm_xpro3", labelKey: "cam_fujifilm_xpro3" },
      { value: "fujifilm_xm5", labelKey: "cam_fujifilm_xm5" },
      { value: "fujifilm_xa7", labelKey: "cam_fujifilm_xa7" },
      { value: "fujifilm_gfx100ii", labelKey: "cam_fujifilm_gfx100ii" },
      { value: "fujifilm_gfx100s", labelKey: "cam_fujifilm_gfx100s" },
      { value: "fujifilm_gfx50sii", labelKey: "cam_fujifilm_gfx50sii" },
      { value: "fujifilm_s5pro", labelKey: "cam_fujifilm_s5pro" },
      { value: "fujifilm_s3pro", labelKey: "cam_fujifilm_s3pro" },
    ],
  },
  {
    brand: "Other",
    cameras: [
      { value: "custom", labelKey: "cam_custom" },
    ],
  },
];

const SENSOR_TYPES = [
  { value: "full_frame", labelKey: "sensor_full_frame" },
  { value: "apsc_canon", labelKey: "sensor_apsc_canon" },
  { value: "apsc_other", labelKey: "sensor_apsc_other" },
  { value: "micro43", labelKey: "sensor_micro43" },
  { value: "one_inch", labelKey: "sensor_one_inch" },
];

const ISO_PERFORMANCE_LEVELS = [
  { value: "excellent", labelKey: "iso_perf_excellent" },
  { value: "good", labelKey: "iso_perf_good" },
  { value: "moderate", labelKey: "iso_perf_moderate" },
  { value: "limited", labelKey: "iso_perf_limited" },
];

const LENS_TYPES = [
  { value: "standard", labelKey: "lens_standard" },
  { value: "wide", labelKey: "lens_wide" },
  { value: "telephoto", labelKey: "lens_telephoto" },
  { value: "macro", labelKey: "lens_macro" },
];

const LIGHTING_TYPES = [
  { value: "natural", labelKey: "lt_natural" },
  { value: "artificial", labelKey: "lt_artificial" },
  { value: "mixed", labelKey: "lt_mixed" },
];

const LIGHT_CONDITIONS = [
  { value: "bright_sun", labelKey: "lc_bright_sun" },
  { value: "cloudy", labelKey: "lc_cloudy" },
  { value: "shade", labelKey: "lc_shade" },
  { value: "golden_hour", labelKey: "lc_golden_hour" },
  { value: "blue_hour", labelKey: "lc_blue_hour" },
  { value: "indoor_window", labelKey: "lc_indoor_window" },
  { value: "indoor_room", labelKey: "lc_indoor_room" },
  { value: "studio", labelKey: "lc_studio" },
  { value: "night_street", labelKey: "lc_night_street" },
  { value: "low_light", labelKey: "lc_low_light" },
];

const TIMES_OF_DAY = [
  { value: "sunrise", labelKey: "tod_sunrise" },
  { value: "morning", labelKey: "tod_morning" },
  { value: "noon", labelKey: "tod_noon" },
  { value: "afternoon", labelKey: "tod_afternoon" },
  { value: "sunset", labelKey: "tod_sunset" },
  { value: "blue_hour", labelKey: "tod_blue_hour" },
  { value: "night", labelKey: "tod_night" },
];

const PHOTO_TYPES = [
  { value: "portrait", labelKey: "pt_portrait" },
  { value: "landscape", labelKey: "pt_landscape" },
  { value: "sports", labelKey: "pt_sports" },
  { value: "macro", labelKey: "pt_macro" },
  { value: "street", labelKey: "pt_street" },
  { value: "wildlife", labelKey: "pt_wildlife" },
  { value: "product", labelKey: "pt_product" },
  { value: "event_indoor", labelKey: "pt_event_indoor" },
  { value: "general", labelKey: "pt_general" },
];

const MOVEMENTS = [
  { value: "still", labelKey: "mv_still" },
  { value: "slow", labelKey: "mv_slow" },
  { value: "fast", labelKey: "mv_fast" },
];

const DOF_OPTIONS = [
  { value: "shallow", labelKey: "dof_shallow" },
  { value: "medium", labelKey: "dof_medium" },
  { value: "deep", labelKey: "dof_deep" },
];

const EVENT_SCENARIOS = [
  { value: "none", labelKey: "event_none" },
  { value: "wedding", labelKey: "event_wedding" },
  { value: "indoor_concert", labelKey: "event_indoor_concert" },
  { value: "outdoor_concert", labelKey: "event_outdoor_concert" },
  { value: "graduation", labelKey: "event_graduation" },
  { value: "sports", labelKey: "event_sports" },
  { value: "birthday", labelKey: "event_birthday" },
  { value: "church", labelKey: "event_church" },
  { value: "family", labelKey: "event_family" },
  { value: "corporate", labelKey: "event_corporate" },
  { value: "night_event", labelKey: "event_night_event" },
  { value: "indoor_studio", labelKey: "event_indoor_studio" },
  { value: "outdoor_studio", labelKey: "event_outdoor_studio" },
];

const PRIMARY_GOALS = [
  { value: "none", labelKey: "goal_none" },
  { value: "portraits", labelKey: "goal_portraits" },
  { value: "candid", labelKey: "goal_candid" },
  { value: "group", labelKey: "goal_group" },
  { value: "stage_action", labelKey: "goal_stage_action" },
  { value: "closeups", labelKey: "goal_closeups" },
  { value: "wide_environmental", labelKey: "goal_wide_environmental" },
  { value: "detail_shots", labelKey: "goal_detail_shots" },
];

const CAMERA_TYPES = [
  { value: "all", en: "All", es: "Todas" },
  { value: "dslr", en: "DSLR", es: "DSLR" },
  { value: "mirrorless", en: "Mirrorless", es: "Mirrorless" },
  { value: "film", en: "Film", es: "Película" },
];

function OptionButtons({ options, value, onChange, testId }) {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2 flex-wrap">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={cn(
            "px-3 py-1.5 rounded-lg text-sm border transition-all duration-200",
            value === opt.value
              ? "bg-[#F5A524] text-[#0A0A0A] border-[#F5A524] font-medium"
              : "bg-[#0A0A0A] text-[#A1A1AA] border-white/10 hover:border-white/20 hover:text-white"
          )}
          data-testid={`${testId}-${opt.value}`}
        >
          {t(opt.labelKey)}
        </button>
      ))}
    </div>
  );
}

export default function SettingsForm({ onCalculate, loading, gear, prefill }) {
  const { t, lang } = useTranslation();
  const [cameraFilter, setCameraFilter] = useState("all");
  const [filmPick, setFilmPick] = useState(FILM_CAMERAS[0].id);
  const pf = prefill || {};
  const [camera, setCamera] = useState(pf.camera_id || "canon_eos_rebel_t3i");
  const [focalLength, setFocalLength] = useState(pf.focal_length != null ? String(pf.focal_length) : "50");
  const [maxAperture, setMaxAperture] = useState(pf.max_aperture != null ? String(pf.max_aperture) : "1.8");
  const [lensType, setLensType] = useState(pf.lens_type || "standard");
  const [lightingType, setLightingType] = useState(pf.lighting_type || "natural");
  const [lightCondition, setLightCondition] = useState(pf.light_condition || "bright_sun");
  const [timeOfDay, setTimeOfDay] = useState(pf.time_of_day || "noon");
  const [photoType, setPhotoType] = useState(pf.photo_type || "portrait");
  const [useTripod, setUseTripod] = useState(typeof pf.use_tripod === "boolean" ? pf.use_tripod : false);
  const [subjectMovement, setSubjectMovement] = useState(pf.subject_movement || "still");
  const [depthOfField, setDepthOfField] = useState(pf.depth_of_field || "medium");
  const [eventScenario, setEventScenario] = useState(pf.event_scenario || "none");
  const [primaryGoal, setPrimaryGoal] = useState(pf.primary_goal || "none");
  const [customSensorType, setCustomSensorType] = useState("apsc_other");
  const [customIsoPerformance, setCustomIsoPerformance] = useState("moderate");

  const hasGear = gear && ((gear.cameras && gear.cameras.length > 0) || (gear.lenses && gear.lenses.length > 0));

  const fillCamera = (cam) => {
    const cameraId = cam.camera_id || cam;
    // Film bodies have no digital sensor profile — 35mm film = full frame, so
    // route them through the custom Full-Frame profile for valid exposure math.
    if (isFilmCameraId(cameraId)) {
      setCamera("custom");
      setCustomSensorType("full_frame");
      setFilmPick(cameraId);
      setCameraFilter("film");
      return;
    }
    setCameraFilter("all");
    setCamera(cameraId);
  };

  const fillLens = (lens) => {
    if (lens.focal_min && lens.focal_max) {
      const mid = lens.focal_min === lens.focal_max ? lens.focal_min : Math.round((lens.focal_min + lens.focal_max) / 2);
      setFocalLength(String(mid));
    }
    if (lens.max_aperture) setMaxAperture(String(lens.max_aperture));
    if (lens.lens_type) setLensType(lens.lens_type);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onCalculate({
      camera_id: camera,
      focal_length: parseFloat(focalLength) || 50,
      max_aperture: parseFloat(maxAperture) || 1.8,
      lens_type: lensType,
      lighting_type: lightingType,
      light_condition: lightCondition,
      time_of_day: timeOfDay,
      photo_type: photoType,
      use_tripod: useTripod,
      subject_movement: subjectMovement,
      depth_of_field: depthOfField,
      event_scenario: eventScenario,
      primary_goal: primaryGoal,
      custom_sensor_type: customSensorType,
      custom_iso_performance: customIsoPerformance,
      film_camera_id: cameraFilter === "film" ? filmPick : null,
    });
  };

  const selectContentClass = "bg-[#121214] border-white/10";

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="settings-form">
      {/* Quick Fill from Gear */}
      {hasGear && (
        <div className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/20 rounded-xl p-4 space-y-3" data-testid="gear-autofill-panel">
          <h3 className="text-white font-['Outfit'] font-medium flex items-center gap-2 text-sm">
            <Backpack weight="regular" className="text-[#F5A524] w-4 h-4" />
            {t("gear_quickfill")}
          </h3>
          {gear.cameras && gear.cameras.length > 0 && (
            <div className="space-y-1">
              <span className="text-[#71717A] text-xs uppercase tracking-[0.15em]">{t("gear_my_cameras")}</span>
              <div className="flex gap-2 flex-wrap">
                {gear.cameras.map((cam, i) => {
                  const camId = cam.camera_id || cam;
                  const isActive = camera === camId;
                  const filmLabel = getFilmCameraLabel(camId);
                  const label = filmLabel
                    ? filmLabel
                    : t(`cam_${camId}`) !== `cam_${camId}`
                    ? t(`cam_${camId}`)
                    : camId;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => fillCamera(cam)}
                      className={cn(
                        "px-3 py-1.5 rounded-lg text-xs border transition-all duration-200",
                        isActive
                          ? "bg-[#F5A524] text-[#0A0A0A] border-[#F5A524] font-medium"
                          : "bg-[#0A0A0A] text-[#A1A1AA] border-white/10 hover:border-[#F5A524]/50 hover:text-white"
                      )}
                      data-testid={`gear-fill-camera-${i}`}
                    >
                      {label}{filmLabel ? <span className="text-[#71717A] ml-1">· {t("film_full_frame_tag")}</span> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {gear.lenses && gear.lenses.length > 0 && (
            <div className="space-y-1">
              <span className="text-[#71717A] text-xs uppercase tracking-[0.15em]">{t("gear_my_lenses")}</span>
              <div className="flex gap-2 flex-wrap">
                {gear.lenses.map((lens, i) => {
                  const focalLabel = lens.focal_min === lens.focal_max ? `${lens.focal_min}mm` : `${lens.focal_min}-${lens.focal_max}mm`;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => fillLens(lens)}
                      className="px-3 py-1.5 rounded-lg text-xs border bg-[#0A0A0A] text-[#A1A1AA] border-white/10 hover:border-[#F5A524]/50 hover:text-white transition-all duration-200"
                      data-testid={`gear-fill-lens-${i}`}
                    >
                      {lens.name} <span className="text-[#71717A] ml-1">{focalLabel} f/{lens.max_aperture}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Camera & Lens */}
      <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5 space-y-4">
        <h3 className="text-white font-['Outfit'] font-medium flex items-center gap-2 text-sm">
          <Camera weight="regular" className="text-[#F5A524] w-4 h-4" />
          {t("form_camera_lens")}
        </h3>

        <div className="space-y-1.5">
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
            {t("form_camera")}
          </Label>
          <div className="flex gap-2 flex-wrap pb-1" data-testid="camera-type-filter">
            {CAMERA_TYPES.map((ct) => (
              <button
                key={ct.value}
                type="button"
                onClick={() => {
                  setCameraFilter(ct.value);
                  if (ct.value === "film") fillCamera(filmPick);
                  else if (ct.value !== "all" && cameraType(camera) !== ct.value) {
                    const first = CAMERA_GROUPS.flatMap((g) => g.cameras).find((c) => cameraType(c.value) === ct.value);
                    setCamera(first.value);
                  }
                }}
                className={cn(
                  "px-3 py-1 rounded-full text-xs border transition-colors duration-200",
                  cameraFilter === ct.value
                    ? "bg-[#F5A524] text-[#0A0A0A] border-[#F5A524] font-medium"
                    : "bg-[#0A0A0A] text-[#A1A1AA] border-white/10 hover:border-[#F5A524]/50 hover:text-white"
                )}
                data-testid={`camera-type-${ct.value}`}
              >
                {lang === "es" ? ct.es : ct.en}
              </button>
            ))}
          </div>
          {cameraFilter === "film" ? (
            <>
              <Select value={filmPick} onValueChange={(v) => { setFilmPick(v); fillCamera(v); }}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524]" data-testid="camera-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={selectContentClass}>
                  {FILM_CAMERAS.map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[#71717A] text-xs" data-testid="film-camera-hint">
                {lang === "es"
                  ? "Película 35mm = Full Frame. Usa el ISO de tu rollo."
                  : "35mm film = Full Frame. Use your film's ISO."}
              </p>
            </>
          ) : (
          <Select key={cameraFilter} value={camera} onValueChange={(v) => v && setCamera(v)}>
            <SelectTrigger
              className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524]"
              data-testid="camera-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={selectContentClass}>
              {CAMERA_GROUPS.map((group) => ({
                ...group,
                cameras: group.cameras.filter((c) => cameraFilter === "all" || [cameraFilter, "any"].includes(cameraType(c.value))),
              }))
                .filter((group) => group.cameras.length > 0)
                .map((group, gi) => (
                <div key={group.brand}>
                  {gi > 0 && <div className="h-px bg-white/10 my-1" />}
                  <div className="px-2 py-1.5 text-xs text-[#71717A] font-semibold tracking-wider">
                    {group.brand}
                  </div>
                  {group.cameras.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {t(c.labelKey)}
                    </SelectItem>
                  ))}
                </div>
              ))}
            </SelectContent>
          </Select>
          )}
        </div>

        {camera === "custom" && cameraFilter !== "film" && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
                {t("custom_sensor_type")}
              </Label>
              <Select value={customSensorType} onValueChange={setCustomSensorType}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524]" data-testid="custom-sensor-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={selectContentClass}>
                  {SENSOR_TYPES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{t(s.labelKey)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
                {t("custom_iso_performance")}
              </Label>
              <Select value={customIsoPerformance} onValueChange={setCustomIsoPerformance}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524]" data-testid="custom-iso-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={selectContentClass}>
                  {ISO_PERFORMANCE_LEVELS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{t(p.labelKey)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
              {t("form_focal_length")} ({t("form_mm")})
            </Label>
            <Input
              type="number"
              min="1"
              max="800"
              value={focalLength}
              onChange={(e) => setFocalLength(e.target.value)}
              className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524] focus:border-[#F5A524]"
              data-testid="focal-length-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
              {t("form_max_aperture")}
            </Label>
            <Input
              type="number"
              min="1"
              max="32"
              step="0.1"
              value={maxAperture}
              onChange={(e) => setMaxAperture(e.target.value)}
              className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524] focus:border-[#F5A524]"
              data-testid="max-aperture-input"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
            {t("form_lens_type")}
          </Label>
          <OptionButtons
            options={LENS_TYPES}
            value={lensType}
            onChange={setLensType}
            testId="lens-type"
          />
        </div>
      </div>

      {/* Scene & Lighting */}
      <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5 space-y-4">
        <h3 className="text-white font-['Outfit'] font-medium flex items-center gap-2 text-sm">
          <Sun weight="regular" className="text-[#F5A524] w-4 h-4" />
          {t("form_scene")}
        </h3>

        <div className="space-y-1.5">
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
            {t("form_lighting_type")}
          </Label>
          <OptionButtons
            options={LIGHTING_TYPES}
            value={lightingType}
            onChange={setLightingType}
            testId="lighting-type"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
            {t("form_light_condition")}
          </Label>
          <Select value={lightCondition} onValueChange={setLightCondition}>
            <SelectTrigger
              className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524]"
              data-testid="light-condition-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={selectContentClass}>
              {LIGHT_CONDITIONS.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {t(c.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
              {t("form_time_of_day")}
            </Label>
            <div className="relative group">
              <Info weight="regular" className="w-3.5 h-3.5 text-[#52525B] cursor-help" data-testid="tod-info-icon" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#1E1E20] border border-white/10 rounded-lg text-xs text-[#A1A1AA] w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50 shadow-xl" data-testid="tod-tooltip">
                {t("form_time_of_day_hint")}
              </div>
            </div>
          </div>
          <Select value={timeOfDay} onValueChange={setTimeOfDay}>
            <SelectTrigger
              className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524]"
              data-testid="time-of-day-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={selectContentClass}>
              {TIMES_OF_DAY.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {t(c.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Photo Type */}
      <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5 space-y-4">
        <h3 className="text-white font-['Outfit'] font-medium flex items-center gap-2 text-sm">
          <ImageSquare weight="regular" className="text-[#F5A524] w-4 h-4" />
          {t("form_photo_type_title")}
        </h3>
        <Select value={photoType} onValueChange={setPhotoType}>
          <SelectTrigger
            className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524]"
            data-testid="photo-type-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={selectContentClass}>
            {PHOTO_TYPES.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {t(c.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Event / Shoot Scenario */}
      <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5 space-y-4">
        <h3 className="text-white font-['Outfit'] font-medium flex items-center gap-2 text-sm">
          <CalendarCheck weight="regular" className="text-[#F5A524] w-4 h-4" />
          {t("form_event_scenario")}
        </h3>
        <Select value={eventScenario} onValueChange={(v) => { setEventScenario(v); if (v === "none") setPrimaryGoal("none"); }}>
          <SelectTrigger
            className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524]"
            data-testid="event-scenario-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={selectContentClass}>
            {EVENT_SCENARIOS.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {t(c.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {eventScenario !== "none" && (
          <div className="space-y-1.5">
            <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
              {t("form_primary_goal")}
            </Label>
            <Select value={primaryGoal} onValueChange={setPrimaryGoal}>
              <SelectTrigger
                className="bg-[#0A0A0A] border-white/10 text-white focus:ring-[#F5A524]"
                data-testid="primary-goal-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={selectContentClass}>
                {PRIMARY_GOALS.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {t(c.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Options */}
      <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5 space-y-4">
        <h3 className="text-white font-['Outfit'] font-medium flex items-center gap-2 text-sm">
          <SlidersHorizontal weight="regular" className="text-[#F5A524] w-4 h-4" />
          {t("form_options")}
        </h3>

        <div className="flex items-center justify-between">
          <Label className="text-[#A1A1AA] text-sm">{t("form_tripod")}</Label>
          <Switch
            checked={useTripod}
            onCheckedChange={setUseTripod}
            className="data-[state=checked]:bg-[#F5A524]"
            data-testid="tripod-switch"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
            {t("form_subject_movement")}
          </Label>
          <OptionButtons
            options={MOVEMENTS}
            value={subjectMovement}
            onChange={setSubjectMovement}
            testId="movement"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
            {t("form_depth_of_field")}
          </Label>
          <OptionButtons
            options={DOF_OPTIONS}
            value={depthOfField}
            onChange={setDepthOfField}
            testId="dof"
          />
        </div>
      </div>

      {/* Calculate Button */}
      <Button
        type="submit"
        disabled={loading}
        className="w-full h-12 bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-semibold text-base active:scale-[0.98] transition-all"
        data-testid="calculate-settings-button"
      >
        {loading ? t("form_calculating") : t("form_calculate")}
      </Button>
    </form>
  );
}
